import { useMemo, useState } from 'react';
import { ChevronRight, Search, Trash2 } from 'lucide-react';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { CluvioOptionInput } from '@/components/CluvioOptionInput';
import { MultiCombobox } from '@/components/MultiCombobox';
import { Field } from '@/components/primitives';
import { TIMERANGE_OPTIONS, AGGREGATION_OPTIONS, isMultiControl, isCheckboxControl } from '@/lib/cluvio';
import { useCluvioFilterOptions, useCreateDashboard, useDeleteDashboard } from '@/api/hooks';
import { api } from '@/api/client';
import { toast } from 'react-hot-toast';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import type { FilterDiff, LintIssue } from '@/lib/template-lint';
import { cn } from '@/lib/utils';

export interface CluvioFilterRef {
  id: string;
  name: string;
  variable_name: string;
  controlType?: string;
}

/** Presets offered when adding a new TIME_RANGE filter to a template. */
const DEFAULT_TIMERANGE_TOKENS = new Set([
  'thisfull_day',
  'previous_day',
  '7_full_day',
  '30_full_day',
  'thisfull_month',
  'previous_month',
]);

interface FilterListEditorProps {
  kind: 'fixed' | 'variable';
  /** Draft array — edited in place and saved via updateDashboardTemplate. */
  items: Record<string, any>[];
  onChange: (items: Record<string, any>[]) => void;
  /** Filters available on the template's Cluvio dashboard (for the Add picker). */
  availableFilters: CluvioFilterRef[];
  /** Keys used by the sibling list — excluded from the Add picker (a filter
   *  must not be both fixed and variable). */
  excludeKeys?: string[];
  /** cluvioFilterID → Cluvio controlType, for cardinality-aware defaults. */
  controlTypeByFilterId?: Record<string, string>;
  /** Lint issues keyed by filter key — shown as amber warnings on the rows. */
  issues?: LintIssue[];
}

/** Renders the correct default-value control for a filter's cardinality:
 * single vs. multi (chips), checkbox toggles, timerange presets, dropdowns. */
function FilterDefaultValue({
  item,
  controlType,
  onUpdate,
}: {
  item: Record<string, any>;
  controlType?: string;
  onUpdate: (patch: Record<string, unknown>) => void;
}) {
  // Multi when Cluvio says so, or when the stored template already holds an array
  const multi = isMultiControl(controlType) || Array.isArray(item.defaultFieldOptions);
  const { data: options, isLoading } = useCluvioFilterOptions(
    item.fieldType === 'CLUVIO_FILTER' ? (item.cluvioFilterID ?? '') : ''
  );

  if (item.fieldType === 'TIME_RANGE') {
    return (
      <Field label="Default time range">
        <CluvioOptionInput
          presets={TIMERANGE_OPTIONS}
          value={item.defaultFieldOption ?? undefined}
          onChange={(opt) => onUpdate({ defaultFieldOption: opt })}
        />
      </Field>
    );
  }

  if (item.fieldType === 'DROPDOWN' && Array.isArray(item.fieldOptions)) {
    // fieldOptions may contain null entries — filter them before rendering/finding
    const validOptions = item.fieldOptions.filter((o: any) => o != null);
    return (
      <Field label="Default option">
        <Select
          value={item.defaultFieldOption?.value ?? ''}
          onChange={(e) => {
            const opt = validOptions.find((o: any) => String(o.value) === e.target.value);
            onUpdate({ defaultFieldOption: opt ?? null });
          }}
          className="[&>select]:h-8 [&>select]:text-xs"
        >
          <option value="">None</option>
          {validOptions.map((o: any) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  if (item.fieldType === 'CHECKBOX' || isCheckboxControl(controlType)) {
    return (
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 accent-primary"
          checked={!!item.defaultCheckboxValue}
          onChange={(e) => onUpdate({ defaultCheckboxValue: e.target.checked })}
        />
        Checked by default
      </label>
    );
  }

  if (item.fieldType === 'INPUT_MULTI') {
    return (
      <Field label="Default values (multi)">
        <MultiCombobox
          values={(item.defaultInputValues ?? []).map((v: string) => ({ label: v, value: v }))}
          onChange={(v) =>
            onUpdate({ defaultInputValues: v.length ? v.map((o) => o.value) : null, defaultInputValue: null })
          }
          options={[]}
          placeholder="Type a value and press Enter…"
        />
      </Field>
    );
  }

  if (multi && item.cluvioFilterID) {
    return (
      <Field label="Default values (multi-select)">
        <MultiCombobox
          values={item.defaultFieldOptions ?? []}
          onChange={(v) => onUpdate({ defaultFieldOptions: v.length ? v : null, defaultFieldOption: null })}
          options={options ?? []}
          loading={isLoading}
          placeholder="Add default values…"
        />
      </Field>
    );
  }

  if (item.fieldType === 'CLUVIO_FILTER' && item.cluvioFilterID) {
    return (
      <Field label="Default value">
        <CluvioOptionInput
          filterId={item.cluvioFilterID}
          value={item.defaultFieldOption ?? undefined}
          onChange={(opt) => onUpdate({ defaultFieldOption: opt })}
        />
      </Field>
    );
  }

  return (
    <Field label="Default value">
      <Input
        value={item.defaultInputValue ?? ''}
        onChange={(e) => onUpdate({ defaultInputValue: e.target.value === '' ? null : e.target.value })}
        className="h-8 text-xs"
      />
    </Field>
  );
}

export function FilterListEditor({ kind, items, onChange, availableFilters, excludeKeys = [], controlTypeByFilterId = {}, issues = [] }: FilterListEditorProps) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const issuesByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const issue of issues) map.set(issue.key, [...(map.get(issue.key) ?? []), issue.message]);
    return map;
  }, [issues]);

  const update = (idx: number, patch: Record<string, unknown>) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeAt = (idx: number) => {
    setExpanded(null);
    onChange(items.filter((_, i) => i !== idx));
  };

  const unusedFilters = useMemo(
    () =>
      availableFilters.filter(
        (af) =>
          !items.some((it) => it.cluvioFilterID === af.id || it.key === af.variable_name) &&
          !excludeKeys.includes(af.variable_name)
      ),
    [availableFilters, items, excludeKeys]
  );

  const addFilter = (filterId: string) => {
    const f = availableFilters.find((x) => x.id === filterId);
    if (!f) return;
    let nf: Record<string, any>;
    if (kind === 'fixed') {
      nf = { fixedType: 'FILTER', key: f.variable_name, label: f.name, cluvioFilterID: f.id };
    } else if (f.controlType === 'date_range' || f.controlType === 'date_time_range') {
      const opts = TIMERANGE_OPTIONS.filter((o) => DEFAULT_TIMERANGE_TOKENS.has(o.value));
      nf = {
        fieldType: 'TIME_RANGE',
        key: f.variable_name,
        label: f.name,
        cluvioFilterID: f.id,
        fieldOptions: opts,
        defaultFieldOption: opts.find((o) => o.value === '7_full_day') ?? opts[0],
        allowCustomTimerange: false,
      };
    } else if (f.controlType === 'date_time_aggregation') {
      nf = {
        fieldType: 'DROPDOWN',
        key: f.variable_name,
        label: f.name,
        fieldOptions: AGGREGATION_OPTIONS,
        defaultFieldOption: AGGREGATION_OPTIONS.find((o) => o.value === 'day'),
      };
    } else {
      nf = {
        fieldType: 'CLUVIO_FILTER',
        key: f.variable_name,
        label: f.name,
        cluvioFilterID: f.id,
        limitFilterOptions: false,
      };
    }
    onChange([...items, nf]);
    setExpanded(items.length);
  };

  const visible = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => (item.label ?? '').toLowerCase().includes(search.toLowerCase()));

  return (
    <Card className="p-5 animate-enter">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold">{kind === 'fixed' ? 'Fixed Filters' : 'Variable Filters'}</h3>
        <Badge variant="secondary">{items.length}</Badge>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search filters..."
          className="pl-9 h-8 text-xs"
        />
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">None — add one below</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">No filters match “{search}”</p>
      ) : (
        <div className="divide-y divide-border/60 max-h-80 overflow-auto">
          {visible.map(({ item, idx }) => {
            const isOpen = expanded === idx;
            const typeLabel = kind === 'fixed' ? item.fixedType : item.fieldType;
            return (
              <div key={`${item.key}-${idx}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : idx)}
                  className="flex w-full items-center gap-2 py-2 px-1 text-left hover:bg-muted/40 transition-colors"
                >
                  <ChevronRight
                    className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                  />
                  {issuesByKey.has(item.key) && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" title={issuesByKey.get(item.key)!.join('\n')} />
                  )}
                  <span className="flex-1 min-w-0 text-xs font-medium truncate" title={item.label}>
                    {item.label}
                  </span>
                  {item.defaultFieldOption?.label && (
                    <span className="text-[10px] text-muted-foreground truncate max-w-[35%]">
                      = {item.defaultFieldOption.label}
                    </span>
                  )}
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {typeLabel}
                  </Badge>
                </button>

                {isOpen && (
                  <div className="px-1 pb-3 pt-1 space-y-2.5 animate-fade-in">
                    {issuesByKey.get(item.key)?.map((msg) => (
                      <p key={msg} className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        {msg}
                      </p>
                    ))}
                    <Field label="Label">
                      <Input
                        value={item.label ?? ''}
                        onChange={(e) => update(idx, { label: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </Field>

                    {kind === 'variable' && (
                      <>
                        <FilterDefaultValue
                          item={item}
                          controlType={item.cluvioFilterID ? controlTypeByFilterId[item.cluvioFilterID] : undefined}
                          onUpdate={(patch) => update(idx, patch)}
                        />

                        <Field label="Placeholder">
                          <Input
                            value={item.placeholder ?? ''}
                            onChange={(e) => update(idx, { placeholder: e.target.value === '' ? null : e.target.value })}
                            className="h-8 text-xs"
                          />
                        </Field>

                        {item.fieldType === 'CLUVIO_FILTER' && (
                          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-primary"
                              checked={!!item.limitFilterOptions}
                              onChange={(e) => update(idx, { limitFilterOptions: e.target.checked })}
                            />
                            Limit filter options
                          </label>
                        )}
                        {item.fieldType === 'TIME_RANGE' && (
                          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-primary"
                              checked={!!item.allowCustomTimerange}
                              onChange={(e) => update(idx, { allowCustomTimerange: e.target.checked })}
                            />
                            Allow custom timeranges
                          </label>
                        )}
                      </>
                    )}

                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={() => removeAt(idx)}
                        className="inline-flex items-center gap-1 text-[11px] text-destructive hover:underline"
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove this filter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {unusedFilters.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value) addFilter(e.target.value);
            }}
            className="[&>select]:h-8 [&>select]:text-xs"
          >
            <option value="">+ Add filter from the Cluvio dashboard…</option>
            {unusedFilters.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Review dialog: humanized diff of filter drafts vs. the stored template
// ---------------------------------------------------------------------------
export function ReviewFiltersDialog({
  open,
  templateName,
  fixedDiff,
  variableDiff,
  pending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  templateName: string;
  fixedDiff: FilterDiff;
  variableDiff: FilterDiff;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const sections: Array<{ title: string; diff: FilterDiff }> = [
    { title: 'Fixed filters', diff: fixedDiff },
    { title: 'Variable filters', diff: variableDiff },
  ];
  return (
    <Dialog
      open={open}
      title={`Review filter changes — ${templateName}`}
      onClose={onCancel}
      busy={pending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
            Back
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={pending}>
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-xs">
        {sections.map(({ title, diff }) => {
          const empty = diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0;
          return (
            <div key={title}>
              <p className="font-semibold mb-1">{title}</p>
              {empty ? (
                <p className="text-muted-foreground">No changes</p>
              ) : (
                <ul className="space-y-1">
                  {diff.added.map((k) => (
                    <li key={`a-${k}`} className="text-green-600 dark:text-green-400">
                      + {k} <span className="text-muted-foreground">(added)</span>
                    </li>
                  ))}
                  {diff.removed.map((k) => (
                    <li key={`r-${k}`} className="text-destructive">
                      − {k} <span className="text-muted-foreground">(removed)</span>
                    </li>
                  ))}
                  {diff.changed.map((k) => (
                    <li key={`c-${k}`} className="text-amber-600 dark:text-amber-400">
                      ~ {k} <span className="text-muted-foreground">(modified)</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        <p className="text-muted-foreground border-t border-border pt-2">
          Applies only to dashboards created from this template in the future.
        </p>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Re-encode: recreate existing dashboards from the (updated) template
// ---------------------------------------------------------------------------
export function ReencodeDialog({
  template,
  open,
  onClose,
  onDone,
}: {
  template: Record<string, any> | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const dashboards: Array<{ id: string; name: string; tenantID: string }> = template?.dashboards?.items ?? [];
  const createDashboard = useCreateDashboard();
  const deleteDashboard = useDeleteDashboard();
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const matches = !!template && confirmText.trim() === template.name;

  const run = async () => {
    if (!template || !matches) return;
    setRunning(true);
    setProgress(0);
    let failed = 0;
    for (const d of dashboards) {
      try {
        // The template's current filter definition applies at encode time;
        // carry over the dashboard's tenant-specific parameters.
        const tenantResp = await api.get<{ data: { getTenant: any } }>(`/tenants/${d.tenantID}`);
        const full = (tenantResp.data.getTenant.dashboards?.items ?? []).find((x: any) => x.id === d.id);
        if (!full) throw new Error('not found in tenant');
        await createDashboard.mutateAsync({
          cluvioDashboardID: template.cluvioDashboard?.id || '',
          sharingToken: template.cluvioSharingToken?.token || '',
          name: full.name,
          sortOrder: full.sortOrder,
          description: full.description,
          typeOfTenantID: full.typeOfTenantID,
          tenantID: full.tenantID,
          templateID: template.id,
          fixedParameters: full.fixedParameters,
          variableFilterOverrides: full.variableFilterOverrides,
        });
        await deleteDashboard.mutateAsync({ id: d.id, auditLabel: `"${full.name}" · re-encoded from template "${template.name}"` });
      } catch {
        failed++;
      }
      setProgress((p) => p + 1);
    }
    setRunning(false);
    if (failed > 0) toast.error(`${failed} of ${dashboards.length} dashboards could not be re-encoded`);
    else toast.success(`${dashboards.length} dashboard${dashboards.length === 1 ? '' : 's'} re-encoded`);
    setConfirmText('');
    onDone();
  };

  return (
    <Dialog
      open={open}
      title={`Re-encode ${dashboards.length} dashboard${dashboards.length === 1 ? '' : 's'}?`}
      onClose={() => {
        if (!running) {
          setConfirmText('');
          onClose();
        }
      }}
      busy={running}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={run} disabled={running || !matches}>
            {running ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                {progress}/{dashboards.length}
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Re-encode all
              </>
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-xs text-muted-foreground">
        <p className="leading-relaxed">
          Each dashboard is recreated from the template's <em>current</em> definition (tenant-specific fixed
          parameters are carried over) and the old one is removed. Dashboards missing values for newly added fixed
          filters may fail.
        </p>
        <p>
          Type <span className="font-medium text-foreground">{template?.name}</span> to confirm:
        </p>
        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Template name" autoFocus />
      </div>
    </Dialog>
  );
}
